import { Injectable } from '@angular/core';
import { DatePipe } from "@angular/common";


const DateUnitLookup = {
    //'second': 1000,
    'minute': 60*1000,
    'm': 60*1000,
    'hour': 60*60*1000,
    'h': 60*60*1000,
    'day': 24*60*60*1000,
    'd': 24*60*60*1000,
    'M':30*24*60*60*1000,
    'month':24*60*60*1000,
    'y':365.25*24*60*60*1000,
    'year':365.25*24*60*60*1000,
};

@Injectable()
export class DateService {

    public static readonly DateFormat = 'MM/dd/yyyy';
    public static readonly DatePickerFormat = 'mm/dd/yyyy';
    private currentDate: Date = new Date();

    constructor(
        public readonly datePipe: DatePipe
    ) { }

    public generateDateWithOffset(yearOffset: number = 0, monthOffset: number = 0, dayOffset: number = 0): Date {
        const dateValues = this.getDateValuesFromOffset(yearOffset, monthOffset, dayOffset)
        return new Date(dateValues.year, dateValues.month, dateValues.day);
    }

    private getDateValuesFromOffset(yearOffset: number, monthOffset: number, dayOffset: number): any {
        const monthAndYear: any = this.getYearMonthAndDayOffset(yearOffset, monthOffset, dayOffset)
        return {
            year: monthAndYear.year,
            month: monthAndYear.month,
            day: monthAndYear.day
        }
    }

    private getYearMonthAndDayOffset(yearOffset: number, monthOffset: number, dayOffset: number): any {
        //Account for the day
        const tempDate: Date = new Date(this.currentDate.toUTCString());
        tempDate.setTime(tempDate.getTime() + (dayOffset * 86400000));
        tempDate.setUTCFullYear(tempDate.getUTCFullYear() + yearOffset);
        tempDate.setUTCMonth(tempDate.getUTCMonth() + monthOffset);

        return {
          year: tempDate.getFullYear(),
          month: tempDate.getMonth(),
          day: tempDate.getDate()
        }
    }

    public convertUtcStringToMMDDYYYY(utcString: string): string {
        let parsedDate = Date.parse(utcString);
        if (isNaN(parsedDate)) return "";
        const date = new Date(utcString);
        return this.getPaddedDateValue((date.getMonth() + 1)) + '-' + this.getPaddedDateValue(date.getDate()) + '-' + date.getFullYear();
    }

    public convertUtcStringToYYYYMMDD(utcString: string): string {
        let parsedDate = Date.parse(utcString);
        if (isNaN(parsedDate)) return "";
        const date = new Date(utcString);
        return date.getFullYear() + '-' + this.getPaddedDateValue((date.getMonth() + 1)) + '-' + this.getPaddedDateValue(date.getDate());
    }

    private getPaddedDateValue(dateNumber: number): string {
        if (dateNumber.toString().length > 1) {
          return dateNumber.toString();
        }
        return ("0" + dateNumber.toString().slice(-2));
    }

    public getLastDayOfMonth(year: number, month: number): number {
        return new Date(year, month, 0).getDate();
    }

    public formatDateString(dateString: string): any {
        const date = new Date(dateString);
        const day: string = date.getDate().toString();
        const month: string = (date.getMonth() + 1).toString();
        const year: string = date.getFullYear().toString();
        return [month, day, year].join('/');
    }

    public static formatDate(date: Date): string {
        var dt = DateService.coerceDate(date);
        const day: string = date.getDate().toString();
        const month: string = (date.getMonth() + 1).toString();
        const year: string = date.getFullYear().toString();
        return [month, day, year].join('/');
    }

    public static coerceDate(it:any): Date {
        return it instanceof Date ? it : typeof(it) === "number" ? new Date(it) : new Date(Date.parse(it.toString()))
    }

    public isDueSoon(dueDate: any, daysOut: number): boolean {
        const dateDue = new Date(dueDate);
        const today = new Date();
        const businessDaysFromToday = this.businessDaysFromDate(today, daysOut);
        return (businessDaysFromToday > dateDue);
    }

    private businessDaysFromDate(date, businessDays): Date {
        var counter = 0, tmp = new Date(date);
        while (businessDays >= 0) {
            tmp.setTime(date.getTime() + counter * 86400000);
            if (DateService.isBusinessDay(tmp)) {
                --businessDays;
            }
            ++counter;
        }
        return tmp;
    }

    public static dateAdd(unit: string, amount: number, date: Date): Date {
        if (unit.toLowerCase() === 'bizday') {
            DateService.addBusinessDays(date, amount);
        } else {
            let ticks = (date.getTime() + (DateUnitLookup[unit] * amount));
            return new Date(ticks);
        }
    }

    public static addBusinessDays(date:Date, days:number): Date {
        const direction = days <=0 ? 1 : -1;
        const target = Math.round(days);
        const dayTicks = 24*60*60*1000 * direction;
        date = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        if (target === 0)
            return date;
        let count = 0;
        let ticks = date.getTime();
        while (count < target) {
            ticks += dayTicks;
            if (DateService.isBusinessDay(new Date(ticks))) {
                count++;
            }
        }
        return new Date(ticks);
    }

    public static now(): Date {
        return new Date();
    }

    public static today(): Date {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth(), now.getDate());
    }

    public static diffBusinessDays(a:Date, b:Date): number {
        a = DateService.getDateOnly(a);
        b = DateService.getDateOnly(b);
        let direction = a <= b? 1 : -1;
        let pointer = a <=b ? a : b;
        let end = a <= b? b : a;
        let count = 0;
        while (pointer < end) {
            if (DateService.isOdnrBusinessDay(pointer)) {
                count++;
            }
            pointer = DateService.dateAdd('day',1, pointer);
        }
        return count * direction;
    }

    public static getDateOnly(dt:Date): Date {
        return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
    }

    public static dateDiff(unit:string, date1: Date, date2: Date): number {
        if (unit.toLowerCase() === 'bizday') {
            return DateService.diffBusinessDays(date1, date2);
        }
        else {
            return (date1.getTime() - date2.getTime()) / DateUnitLookup[unit];
        }
    }

    public static datePart(part:string, date1:Date): number {
        if (part === 'y' || part === 'Y' || part === 'yyyy'|| part === 'YYYY' || part.toLowerCase() === 'year')
            return date1.getFullYear();
        if (part === 'M' || part === 'MM' || part.toLowerCase() === 'month')
            return date1.getMonth();
        if (part === 'd'|| part.toLowerCase() === 'day' )
            return date1.getDay() + 1;
        if (part === 'h' || part.toLowerCase() === 'hour')
            return date1.getHours();
        if (part === 'm' || part.toLowerCase() === 'minute')
            return date1.getMinutes();
        throw Error(`unable to get date part for ${part}`);
    }

    private static isBusinessDay(date): boolean {
        return DateService.isOdnrBusinessDay(date);
    }

    private static isOdnrBusinessDay(date): boolean {
        const dayOfWeek = date.getDay();

        // Weekend
        if (dayOfWeek === 0 || dayOfWeek === 6)
            return false;

        const holidays = [
            '12/31+5', // New Year's Day on a saturday celebrated on previous friday
            '1/1',     // New Year's Day
            '1/2+1',   // New Year's Day on a sunday celebrated on next monday
            '1-3/1',   // Birthday of Martin Luther King, third Monday in January
            '2-3/1',   // Washington's Birthday, third Monday in February
            '5~1/1',   // Memorial Day, last Monday in May
            '7/3+5',   // Independence Day
            '7/4',     // Independence Day
            '7/5+1',   // Independence Day
            '9-1/1',   // Labor Day, first Monday in September
            '10-2/1',  // Columbus Day, second Monday in October
            '11/10+5', // Veterans Day
            '11/11',   // Veterans Day
            '11/12+1', // Veterans Day
            '11-4/4',  // Thanksgiving Day, fourth Thursday in November
            '12/24+5', // Christmas Day
            '12/25',   // Christmas Day
            '12/26+1',  // Christmas Day
        ];

        const dayOfMonth = date.getDate(),
            month = date.getMonth() + 1,
            monthDay = month + '/' + dayOfMonth;

        if (holidays.indexOf(monthDay) > -1) {
            return false;
        }

        const monthDayDay = monthDay + '+' + dayOfWeek;
        if (holidays.indexOf(monthDayDay) > -1) {
            return false;
        }

        const weekOfMonth = Math.floor((dayOfMonth - 1) / 7) + 1,
            monthWeekDay = month + '-' + weekOfMonth + '/' + dayOfWeek;
        if (holidays.indexOf(monthWeekDay) > -1) {
            return false;
        }

        const lastDayOfMonth = new Date(date);
        lastDayOfMonth.setMonth(lastDayOfMonth.getMonth() + 1);
        lastDayOfMonth.setDate(0);
        const negWeekOfMonth = Math.floor((lastDayOfMonth.getDate() - dayOfMonth - 1) / 7) + 1,
            monthNegWeekDay = month + '~' + negWeekOfMonth + '/' + dayOfWeek;
        if (holidays.indexOf(monthNegWeekDay) > -1) {
            return false;
        }

        return true;
    }

}
